import { useNavigate, useLocation } from 'react-router-dom'
import { Bell, RefreshCw } from 'lucide-react'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, getInitials } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/store/authStore'
import { authApi } from '@/lib/api/auth'
import apiClient from '@/lib/api/client'
import type { ApiResponse } from '@portal/types'

// ─── Route → page title ───────────────────────────────────────────────────────

const PAGE_TITLES: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/payments': 'Payments',
  '/approvals': 'Approvals',
  '/vendors': 'Vendors',
  '/account': 'Account',
  '/settings': 'Settings',
}

export default function TopBar() {
  const location = useLocation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const clearAuth = useAuthStore((s) => s.clearAuth)

  const title = PAGE_TITLES[location.pathname] ?? 'Portal'

  const { data: pendingCount } = useQuery({
    queryKey: ['approvals-count'],
    queryFn: () => apiClient.get('/api/approvals/pending/count') as unknown as Promise<ApiResponse<{ count: number }>>,
    select: (res) => res.data?.count ?? 0,
    staleTime: 60_000,
  })

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem('portal_refresh_token') ?? ''
    try { await authApi.logout(refreshToken) } catch { /* ignore */ }
    clearAuth()
    navigate('/login', { replace: true })
  }

  const handleRefresh = () => {
    qc.invalidateQueries()
  }

  return (
    <header className="flex h-[60px] shrink-0 items-center justify-between border-b bg-white px-6">
      {/* Page title */}
      <h1 className="text-lg font-semibold text-foreground">{title}</h1>

      {/* Right controls */}
      <div className="flex items-center gap-1">
        {/* Refresh */}
        <button
          type="button"
          onClick={handleRefresh}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title="Refresh data"
        >
          <RefreshCw className="h-4 w-4" />
        </button>

        {/* Bell */}
        <button
          type="button"
          onClick={() => navigate('/approvals')}
          className="relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          title="Pending approvals"
        >
          <Bell className="h-4 w-4" />
          {(pendingCount ?? 0) > 0 && (
            <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-400" />
          )}
        </button>

        {/* Divider */}
        <div className="mx-2 h-5 w-px bg-border" />

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-accent transition-colors"
            >
              <Avatar size="sm">
                <AvatarFallback className="text-[11px]">
                  {user ? getInitials(user.name) : 'U'}
                </AvatarFallback>
              </Avatar>
              <span className={cn('hidden text-sm font-medium sm:block', !user && 'opacity-50')}>
                {user?.name ?? 'Loading…'}
              </span>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              <p className="font-medium text-foreground">{user?.name}</p>
              <p className="text-xs font-normal text-muted-foreground">{user?.email}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <span>Profile Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="text-destructive hover:text-destructive focus:text-destructive"
              onClick={handleLogout}
            >
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
